import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import type { User } from '../types/users.types';
import type { CerrarSesionesRespuesta } from '../../negocio/negocio.types';
import { SesionesService } from '../services/sesiones.service';
import { armarMotivo, esCuentaCompartida, MOTIVOS } from '../utils/sesiones';
import { codigoDe, mensajeDeError } from '../../negocio/errores';
import { MotivoCampos } from './MotivoCampos';

interface Props {
  usuario: User | null;
  onHide: () => void;
  onHecho: (r: CerrarSesionesRespuesta) => void;
}

/**
 * «Cerrar sesión en todos sus equipos» (Consulta CM, fase 0). Conserva la
 * cuenta: corta los tokens emitidos antes de este momento (en cada petición y
 * al renovar) y las conexiones en vivo de los equipos registrados.
 *
 * Cuenta compartida (tienda o bodega): saca a TODOS los equipos de la tienda,
 * así que pide una confirmación aparte. Si la API dice que es compartida
 * aunque el rol de aquí no lo parezca (409), se pide igual.
 */
export const CerrarSesionesDialog: React.FC<Props> = ({ usuario, onHide, onHecho }) => {
  const [motivo, setMotivo] = useState<string>(MOTIVOS[0]);
  const [nota, setNota] = useState('');
  const [confirmo, setConfirmo] = useState(false);
  const [forzarCompartida, setForzarCompartida] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    setMotivo(MOTIVOS[0]);
    setNota('');
    setConfirmo(false);
    setForzarCompartida(false);
    setError(null);
  }, [usuario?.id]);

  if (!usuario) return null;
  const compartida = forzarCompartida || esCuentaCompartida(usuario.role);

  const enviar = async () => {
    setOcupado(true);
    setError(null);
    try {
      const r = await SesionesService.cerrarSesiones(usuario.id, {
        motivo: armarMotivo(motivo, nota),
        ...(compartida ? { confirmarCuentaCompartida: true } : {}),
      });
      onHecho(r);
    } catch (e) {
      if (codigoDe(e) === 'CONFIRMAR_CUENTA_COMPARTIDA') {
        setForzarCompartida(true);
        setConfirmo(false);
      } else {
        setError(mensajeDeError(e));
      }
    } finally {
      setOcupado(false);
    }
  };

  const pie = (
    <div className="flex justify-end gap-2">
      <Button label="Cancelar" className="p-button-text" onClick={onHide} disabled={ocupado} />
      <Button
        label="Cerrar sesiones"
        icon="pi pi-sign-out"
        className="p-button-danger"
        onClick={() => void enviar()}
        loading={ocupado}
        disabled={ocupado || (compartida && !confirmo)}
      />
    </div>
  );

  return (
    <Dialog
      header="Cerrar sesión en todos sus equipos"
      visible
      onHide={onHide}
      footer={pie}
      style={{ width: '32rem' }}
      breakpoints={{ '640px': '95vw' }}
      dismissableMask={!ocupado}
    >
      <div className="space-y-4 text-sm text-gray-700">
        <p>
          <b>{usuario.name || usuario.email}</b> tendrá que volver a iniciar sesión en Consulta CM, el admin y cualquier
          teléfono. La cuenta sigue activa.
        </p>
        {compartida && (
          <div className="rounded-md border border-solid border-amber-300 bg-amber-50 p-3 text-amber-900">
            <p>
              ⚠️ Esta cuenta es compartida: esto saca a TODOS los equipos de la tienda. Para un solo equipo, use
              Dispositivos.
            </p>
            <label className="mt-2 flex items-center gap-2 font-medium">
              <input type="checkbox" checked={confirmo} onChange={(e) => setConfirmo(e.target.checked)} />
              Entiendo: saca a todos los equipos de la tienda
            </label>
          </div>
        )}
        <MotivoCampos motivo={motivo} nota={nota} onMotivo={setMotivo} onNota={setNota} disabled={ocupado} />
        {error && <Message severity="error" text={error} className="w-full justify-start" />}
      </div>
    </Dialog>
  );
};
